import { buildMeasurement, getGeoLogTagsFromActivity } from 'core/service/measurements/measurementUtils';
import { logInfo, logWarn } from 'core-framework/util/LogMessage';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { Activity } from 'gql/autogen/Activity';
import { MeasurementName } from 'core/constant/MeasurementName';
import { IMeasurementTags } from '@agoda/messaging-client';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { sendMeasurement } from 'core/messaging/measurement/measurement';
import { daysFromToday } from 'core-framework/util/datetime/date';
import { Offer } from 'gql/autogen/Offer';
import { ActivityGqlOperation, GqlApiError } from 'store/api/api.types';
import { ActivitiesPageTypes } from 'core/constant/analytics/AnalyticsPageType';
import { MeasurementStatus } from 'core/constant/MeasurementStatus';
import { FieldValue } from 'core/constant/FieldValue';
import { toSafeString } from 'store/api/apiUtils';
import { SearchErrorCode } from 'gql/autogen/SearchErrorCode';
import { SearchSubErrorCode } from 'gql/autogen/SearchSubErrorCode';

const sendAvailabilityGqlMeasurements = (
    message: string,
    measurementName: MeasurementName,
    pageTypeId: ActivitiesPageTypes,
    config: BootstrapConfig,
    tags: IMeasurementTags,
    activityFromDetail?: ActivityWithContent,
): boolean => {
    const measurement = buildMeasurement(ActivityGqlOperation.availability, measurementName, pageTypeId, message, 1, false, config);
    measurement.tags = {
        ...measurement.tags,
        ...tags,
    };
    measurement.tags = getGeoLogTagsFromActivity(measurement.tags, activityFromDetail);
    sendMeasurement({ ...measurement, name: MeasurementName.GqlAvailabilityStatus }, pageTypeId);

    if (measurementName !== MeasurementName.GqlAvailabilityError) {
        logInfo(message, pageTypeId, measurement.tags);
        return true;
    }
    logWarn(message, pageTypeId, measurement.tags);
    return true;
};

const buildAvailabilityMeasurementTags = (activityId: number, activity: Activity = {}, activityFromDetail: ActivityWithContent = {}, error?: GqlApiError): IMeasurementTags => {
    const travelDate = activity.offerPricing?.travelDate ?? '';
    const travelDateDiff = daysFromToday(travelDate);
    const supplierId = activity.masterSupplierId || activityFromDetail.masterSupplierId;
    return {
        travelDate: travelDate || FieldValue.Undefined,
        travelDateDiff: travelDateDiff || FieldValue.Undefined,
        activityId: activityId.toString(),
        supplierId: supplierId ? String(supplierId) : FieldValue.Undefined,
        availableOffers: FieldValue.Zero,
        unavailableOffers: FieldValue.Zero,
        title: activityFromDetail.content?.activity?.title ?? FieldValue.Undefined,
        errorCode: error?.errorCode || SearchErrorCode.EMPTY_ERROR_CODE,
        subErrorCode: error?.subErrorCode || SearchSubErrorCode.EMPTY_SUB_ERROR_CODE,
    };
};

export enum AvailabilityMeasurementMessage {
    Success = 'success',
    NoOffers = 'no offers',
    NoAvailability = 'only unavailable offers',
}

export const measureAvailabilityApiResponse = (
    isSuccess: boolean,
    activityId: number,
    pageTypeId: ActivitiesPageTypes,
    config: BootstrapConfig,
    error?: GqlApiError,
    activity?: Activity,
    activityFromDetail?: ActivityWithContent,
): boolean => {
    const measurementTags = buildAvailabilityMeasurementTags(activityId, activity, activityFromDetail, error);
    const messageLowerCase = toSafeString(error?.message).toLowerCase();
    measurementTags.statusCode = toSafeString(error?.status);
    if (isSuccess) {
        const offers = activity?.offerPricing?.offers || [];
        const availableOffers = offers.filter((offer: Offer) => offer && offer.isAvailable && offer.enrichedOfferOption && offer.enrichedOfferOption.length > 0).length;
        const unavailableOffers = offers.filter((offer: Offer) => offer && !offer.isAvailable && offer.enrichedOfferOption && offer.enrichedOfferOption.length > 0).length;
        const totalOffers = availableOffers + unavailableOffers;

        measurementTags.availableOffers = String(availableOffers);
        measurementTags.unavailableOffers = String(unavailableOffers);
        measurementTags.status = MeasurementStatus.Success;

        if (totalOffers === 0) {
            return sendAvailabilityGqlMeasurements(AvailabilityMeasurementMessage.NoOffers, MeasurementName.GqlAvailabilityNoOffer, pageTypeId, config, measurementTags);
        }
        if (availableOffers === 0) {
            if (unavailableOffers > 0) {
                return sendAvailabilityGqlMeasurements(
                    AvailabilityMeasurementMessage.NoAvailability,
                    MeasurementName.GqlAvailabilityOnlyUnavailableOffer,
                    pageTypeId,
                    config,
                    measurementTags,
                );
            }
        }
        return sendAvailabilityGqlMeasurements(AvailabilityMeasurementMessage.Success, MeasurementName.GqlAvailabilitySuccess, pageTypeId, config, measurementTags);
    }

    measurementTags.status = MeasurementStatus.Failed;
    return sendAvailabilityGqlMeasurements(messageLowerCase, MeasurementName.GqlAvailabilityError, pageTypeId, config, measurementTags);
};
